import { shallowRef, computed, type ShallowRef } from 'vue';

type WithId = { id: string | number };

export function useArrayOperations<T extends WithId>(initialArray: T[] = []) {
  const array: ShallowRef<T[]> = shallowRef([...initialArray]);

  const getById = (id: T['id']) => {
    return computed(() => array.value.find(item => item.id === id));
  };

  const findIndexById = (id: T['id']) => {
    return array.value.findIndex(item => item.id === id);
  };

  const add = (items: T | T[]): void => {
    const newItems = Array.isArray(items) ? items : [items];
    array.value = [...array.value, ...newItems];
  };

  const removeById = (id: T['id']): boolean => {
    const index = findIndexById(id);
    if (index > -1) {
      array.value = [
        ...array.value.slice(0, index),
        ...array.value.slice(index + 1)
      ];
      return true;
    }
    return false;
  };

  const removeMultipleByIds = (ids: T['id'][]): void => {
    array.value = array.value.filter(item => !ids.includes(item.id));
  };

  const updateById = (id: T['id'], updater: (item: T) => T): boolean => {
    const index = findIndexById(id);
    if (index > -1) {
      const newArray = [...array.value];
      newArray[index] = updater(newArray[index]);
      array.value = newArray;
      return true;
    }
    return false;
  };

  const reset = (): void => {
    array.value = [...initialArray];
  };

  return {
    array: computed(() => array.value),
    getById,
    findIndexById,
    add,
    removeById,
    removeMultipleByIds,
    updateById,
    reset
  };
}
